import type {
  IDraftItem,
  ILiveDish,
  ILiveOther,
  TDraftOp,
} from '@/interfaces/IMenuImport';
import {
  HOLD_FLAGS,
  isHeld,
  linkedOutcome,
  liveItemState,
  todayPrice,
} from './live-update';

/**
 * What the workspace shows on a dish of an update session (plan §3.5): the
 * row of a dish already on the menu (its menu name, prices and quiet
 * sub-lines), its price-only editor, and the amber live line with its
 * buttons. Pure; the rules and counts live in `live-update.ts`.
 */

/** A price of a linked dish: today's, and the new one when publishing changes it. */
export interface ILivePrice {
  today: number;
  /** null: publishing leaves it as it is (unchanged, or held). */
  next: number | null;
}

/** A size badge of a linked dish: a size of the menu, or one only the photo has. */
export interface ILiveSizeBadge {
  key: string;
  label: string;
  /** null: a size the photo adds («Nuevo»). */
  today: number | null;
  /** null: publishing leaves it as it is. */
  next: number | null;
}

export interface ILiveRowView {
  /** The name diners see (publishing never changes it). */
  name: string;
  /** Dishes without sizes (one size counts as none); null when the sizes carry the prices. */
  price: ILivePrice | null;
  sizes: ILiveSizeBadge[];
}

/**
 * What the row of a linked dish shows: the menu's name and prices, with the
 * new prices publishing writes (a held dish shows today's only); null when
 * the dish is not linked.
 */
export function liveRowView(item: IDraftItem): ILiveRowView | null {
  const live = item.live;
  if (!live || live.dish === null) return null;
  const dish = live.dish;
  const changes = isHeld(item) ? [] : live.changes;
  if (dish.sizes.length < 2) {
    const change = changes.find((candidate) => candidate.kind !== 'newSize');
    return {
      name: dish.name,
      price: { today: todayPrice(dish), next: change ? change.to : null },
      sizes: [],
    };
  }
  const sizes: ILiveSizeBadge[] = dish.sizes.map((size) => {
    const change = changes.find(
      (candidate) =>
        candidate.kind === 'size' &&
        candidate.optionItemId === size.optionItemId
    );
    return {
      key: `live-${size.optionItemId}`,
      label: size.label,
      today: size.price,
      next: change ? change.to : null,
    };
  });
  changes.forEach((change, index) => {
    if (change.kind === 'newSize')
      sizes.push({
        key: `new-${index}`,
        label: change.label,
        today: null,
        next: change.to,
      });
  });
  return { name: dish.name, price: null, sizes };
}

/**
 * The visibility line of a linked dish: hidden on the menu and staying so
 * («Oculto en su menú»), hidden on the menu and shown again («Vuelve a
 * mostrarse»), or hidden by publishing (today's «Oculto»).
 */
export type TLiveVisibility = 'stillHidden' | 'willShow' | 'willHide';

export interface ILiveRowLines {
  visibility: TLiveVisibility | null;
  /** «Mostrarlo»: the dish is hidden in the list, so the owner can show it. */
  canShow: boolean;
  /** «No vimos el precio en la foto: queda {price}»: today's price, else null. */
  noPhotoPrice: number | null;
}

/** The sub-lines under a linked dish; null when the dish is not linked. */
export function liveRowLines(item: IDraftItem): ILiveRowLines | null {
  const live = item.live;
  if (!live || live.dish === null) return null;
  const dish = live.dish;
  const outcome = linkedOutcome(item, dish);
  let visibility: TLiveVisibility | null = null;
  if (!dish.visible) visibility = outcome.visible ? 'willShow' : 'stillHidden';
  else if (!outcome.visible) visibility = 'willHide';
  const noPhotoPrice =
    item.sizes.length === 0 &&
    dish.sizes.length < 2 &&
    item.price === null &&
    live.accepted === undefined
      ? todayPrice(dish)
      : null;
  return {
    visibility,
    canShow: !dish.visible && item.hidden,
    noPhotoPrice,
  };
}

/** One side has sizes and the other not: the editor shows today's prices read-only. */
export function liveShapesDiffer(item: IDraftItem): boolean {
  const dish = item.live?.dish;
  if (!dish) return false;
  return item.sizes.length >= 2 !== dish.sizes.length >= 2;
}

/** Today's price of each draft size, paired through `live.sizeIds` (null: a new size). */
export function liveSizeToday(item: IDraftItem): (number | null)[] {
  const live = item.live;
  const dish = live?.dish;
  if (!live || !dish) return item.sizes.map(() => null);
  const ids = live.sizeIds ?? [];
  return item.sizes.map((_, index) => {
    const id = ids[index];
    const size =
      id === null || id === undefined
        ? undefined
        : dish.sizes.find((candidate) => candidate.optionItemId === id);
    return size ? size.price : null;
  });
}

/**
 * «Dejar el precio de hoy» has something to drop: a price read from the
 * photo (or a size price) that changes it, and a price today above 0.
 */
export function canKeepToday(item: IDraftItem): boolean {
  const dish = item.live?.dish;
  if (!dish || todayPrice(dish) <= 0 || liveShapesDiffer(item)) return false;
  const state = liveItemState(item);
  if (state !== 'priceChanged' && state !== 'held') return false;
  return item.sizes.length > 0
    ? item.sizes.some((size) => size.price !== null)
    : item.price !== null;
}

// ---------------------------------------------------------------------------
// The live line (§3.5)
// ---------------------------------------------------------------------------

export type TLiveActionKind =
  | 'link'
  | 'new'
  | 'confirm'
  | 'keepPrice'
  | 'keepSizes';

export interface ILiveLineAction {
  /** i18n key of the button, or null when `label` is the text. */
  key: string | null;
  /** A ready-made label («Ravioles · $ 11.900»), or null. */
  label: string | null;
  params: Record<string, string>;
  kind: TLiveActionKind;
  /** `link` only. */
  liveItemId?: number;
}

export interface ILiveLineModel {
  textKey: string;
  params: Record<string, string>;
  actions: ILiveLineAction[];
}

export interface ILiveMoneyFormat {
  money(value: number): string;
}

function action(
  key: string,
  kind: TLiveActionKind,
  params: Record<string, string> = {}
): ILiveLineAction {
  return { key, label: null, params, kind };
}

function suggestionLine(
  item: IDraftItem,
  others: readonly ILiveOther[],
  format: ILiveMoneyFormat
): ILiveLineModel | null {
  const candidates = (item.live ? item.live.candidateIds : [])
    .map((id) => others.find((other) => other.itemId === id))
    .filter((other): other is ILiveOther => other !== undefined);
  if (candidates.length === 0) return null;
  const notThis = action('menuImport.update.sameNo', 'new');
  if (candidates.length === 1) {
    const [only] = candidates;
    return {
      textKey: 'menuImport.update.suggestOne',
      params: { name: only.name, price: format.money(only.price) },
      actions: [
        {
          ...action('menuImport.update.sameYes', 'link'),
          liveItemId: only.itemId,
        },
        notThis,
      ],
    };
  }
  return {
    textKey: 'menuImport.update.suggestMany',
    params: {},
    actions: [
      ...candidates.map(
        (candidate): ILiveLineAction => ({
          key: null,
          label: `${candidate.name} · ${format.money(candidate.price)}`,
          params: {},
          kind: 'link',
          liveItemId: candidate.itemId,
        })
      ),
      notThis,
    ],
  };
}

function holdLine(
  item: IDraftItem,
  dish: ILiveDish,
  format: ILiveMoneyFormat
): ILiveLineModel | null {
  const flag = HOLD_FLAGS.find((hold) => item.flags.includes(hold));
  if (!flag) return null;
  const today = todayPrice(dish);
  const sized = item.sizes.length >= 2;
  const confirm = (key: string, params: Record<string, string> = {}) =>
    action(key, 'confirm', params);
  if (flag === 'price_promo')
    return {
      textKey: 'menuImport.update.pricePromo',
      params: {},
      actions: [confirm('menuImport.item.looksGood')],
    };
  if (flag === 'sizes_differ') {
    if (item.live && item.live.changes.length === 0)
      return {
        textKey: 'menuImport.update.sizesMismatch',
        params: {},
        actions: [confirm('menuImport.item.looksGood')],
      };
    return {
      textKey: 'menuImport.update.sizesGuess',
      params: {},
      actions: [
        confirm('menuImport.update.useSizes'),
        ...(today > 0
          ? [action('menuImport.update.keepSizes', 'keepSizes')]
          : []),
      ],
    };
  }
  const down = flag === 'price_down';
  if (sized)
    return {
      textKey: down
        ? 'menuImport.update.priceDownSizes'
        : 'menuImport.update.priceJumpSizes',
      params: {},
      actions: [
        confirm('menuImport.update.useSizes'),
        ...(today > 0
          ? [action('menuImport.update.keepSizes', 'keepSizes')]
          : []),
      ],
    };
  const photo = item.price === null ? '' : format.money(item.price);
  return {
    textKey: down
      ? 'menuImport.update.priceDown'
      : 'menuImport.update.priceJump',
    params: { photo },
    actions: [
      confirm('menuImport.update.use', { price: photo }),
      ...(today > 0
        ? [
            action('menuImport.update.keep', 'keepPrice', {
              price: format.money(today),
            }),
          ]
        : []),
    ],
  };
}

/**
 * The amber line of an update dish (§3.5 «The live line»): a suggestion
 * (its candidates are among `others`, `draft.live.others`) or a hold,
 * with its buttons; null when the dish has neither.
 */
export function liveLineModel(
  item: IDraftItem,
  others: readonly ILiveOther[],
  format: ILiveMoneyFormat
): ILiveLineModel | null {
  const live = item.live;
  if (!live) return null;
  if (live.dish === null) return suggestionLine(item, others, format);
  return isHeld(item) ? holdLine(item, live.dish, format) : null;
}

/** «Dejar {price}»: the photo's price is dropped, today's stays. */
export function keepPriceOp(item: IDraftItem): TDraftOp {
  return { op: 'updateItem', itemId: item.id, price: null };
}

/** «Dejar los de hoy»: the photo's size prices are dropped, today's stay. */
export function keepSizesOp(item: IDraftItem): TDraftOp {
  return {
    op: 'setSizes',
    itemId: item.id,
    sizes: item.sizes.map((size) => ({ label: size.label, price: null })),
  };
}
